import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CheckSquare, FileText, Folder, RefreshCw, Star, Target } from 'lucide-react'
import { toast } from 'sonner'
import { useNavigate } from '@tanstack/react-router'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { StarredDocument } from '@/lib/db/rxdb/hooks'
import type { StarrableEntityType } from '@/lib/types/starred'
import { useSidebar } from '@/components/ui/sidebar'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  useCycles,
  usePages,
  useProjectMutations,
  useProjects,
  useRootProjects,
  useSpaceOrders,
  useStarredItems,
  useStarredMutations,
  useTasks,
} from '@/lib/db/rxdb/hooks'
import { sortSpacesByPrivateOrder } from '@/lib/utils/spaceOrder'
import { useSpaceReorder } from '@/routes/features/projects/hooks/useSpaceReorder'
import { UnifiedIconPicker } from '@/components/app/pickers/unified-icon-picker'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { SectionHeader, rowClass } from '@/components/app/sidebars/spaces/SidebarRows'
import { SpacesList, SpacesRail } from '@/components/app/sidebars/spaces/SpacesList'
import { docRoute } from '@/routes/features/pages/utils/docRoute'
import { PinnedViewsSection } from '@/components/features/saved-filters'

// ─── Form schema ─────────────────────────────────────────────────────────────

type TFunc = (key: string) => string

const buildSpaceFormSchema = (t: TFunc) =>
  z.object({
    name: z
      .string()
      .min(1, t('sidebar.formErrors.spaceNameRequired'))
      .max(100, t('sidebar.formErrors.spaceNameTooLong')),
    icon: z.string().nullable(),
    iconType: z.string().nullable(),
    iconColor: z.string().nullable(),
    projectName: z
      .string()
      .min(1, t('sidebar.formErrors.projectNameRequired'))
      .max(100, t('sidebar.formErrors.projectNameTooLong')),
    projectKey: z
      .string()
      .min(1, t('sidebar.formErrors.projectKeyRequired'))
      .max(10, t('sidebar.formErrors.projectKeyTooLong'))
      .regex(/^[A-Z0-9]+$/, t('sidebar.formErrors.projectKeyFormat')),
    projectIcon: z.string().nullable(),
    projectIconType: z.string().nullable(),
    projectIconColor: z.string().nullable(),
  })

type SpaceFormData = z.infer<ReturnType<typeof buildSpaceFormSchema>>

// Derive a unique URL slug for a new space from its name. spaceKey is no longer
// user-facing — it's only used in routes (/spaces/$spaceKey/...) — so we just
// need something deterministic and unique within the workspace.
function deriveUniqueSpaceKey(name: string, existing: ReadonlyArray<{ projectKey: string }>): string {
  const base = name.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) || 'SPACE'
  const taken = new Set(existing.map((s) => s.projectKey.toUpperCase()))
  if (!taken.has(base)) return base
  for (let n = 2; n < 1000; n++) {
    const suffix = String(n)
    const candidate = `${base.slice(0, Math.max(1, 10 - suffix.length))}${suffix}`
    if (!taken.has(candidate)) return candidate
  }
  return `${base.slice(0, 4)}${crypto.randomUUID().slice(0, 6).toUpperCase()}`
}

interface ProjectSidebarProps {
  workspaceId: string | null
  workspaceSlug: string | null
  /** The space the current route is in. */
  routeSpaceId: string | null
  /** Replace the sidebar with this space (design 21 C). */
  onDrillIn: (spaceId: string) => void
}

// ─── Favorites section ───────────────────────────────────────────────────────

interface ResolvedFavorite {
  id: string
  entityType: StarrableEntityType
  entityId: string
  name: string
  icon: React.ReactNode
  onClick: () => void
  starred: StarredDocument
}

function FavoritesSection({ workspaceSlug }: { workspaceSlug: string }) {
  const { t } = useTranslation('projects')
  const navigate = useNavigate()
  const { starredItems, isLoading } = useStarredItems()
  const { projects } = useProjects()
  const { tasks } = useTasks()
  const { pages } = usePages()
  const { cycles } = useCycles()
  const { toggleStar } = useStarredMutations()

  const resolved = useMemo<Array<ResolvedFavorite>>(() => {
    if (starredItems.length === 0) return []

    const projectMap = new Map((projects ?? []).filter((p) => !p.isDeleted).map((p) => [p.id, p]))
    const taskMap = new Map((tasks ?? []).filter((t) => !t.isDeleted).map((t) => [t.id, t]))
    const pageMap = new Map((pages ?? []).filter((p) => !p.isDeleted).map((p) => [p.id, p]))
    const cycleMap = new Map((cycles ?? []).filter((c) => !c.isDeleted).map((c) => [c.id, c]))

    const results: Array<ResolvedFavorite> = []

    for (const starred of starredItems) {
      if (starred.entityType === 'project') {
        const project = projectMap.get(starred.entityId)
        if (!project) continue
        results.push({
          id: starred.id,
          entityType: 'project',
          entityId: project.id,
          name: project.name,
          icon: <ProjectIcon iconType={project.iconType} icon={project.icon} iconColor={project.iconColor} size={14} />,
          onClick: () => navigate({
            to: '/$workspaceSlug/spaces/$spaceKey',
            params: { workspaceSlug, spaceKey: project.projectKey.toLowerCase() },
          }),
          starred,
        })
      } else if (starred.entityType === 'task') {
        const task = taskMap.get(starred.entityId)
        if (!task) continue
        results.push({
          id: starred.id,
          entityType: 'task',
          entityId: task.id,
          name: task.name,
          icon: <CheckSquare className="size-3.5 text-muted-foreground shrink-0" />,
          onClick: () => navigate({
            to: '/$workspaceSlug/tasks/$taskId',
            params: { workspaceSlug, taskId: task.id },
          }),
          starred,
        })
      } else if (starred.entityType === 'page') {
        const page = pageMap.get(starred.entityId)
        if (!page) continue
        const parentProject = projectMap.get(page.projectId)
        if (!parentProject) continue
        results.push({
          id: starred.id,
          entityType: 'page',
          entityId: page.id,
          name: page.title || t('sidebar.doc.untitled'),
          icon: <FileText className="size-3.5 text-muted-foreground shrink-0" />,
          onClick: () => {
            const route = docRoute(workspaceSlug, page, projects ?? [])
            if (route) void navigate(route)
            else void navigate({
              to: '/$workspaceSlug/projects/$projectKey/pages/$pageSlug',
              params: { workspaceSlug, projectKey: parentProject.projectKey.toLowerCase(), pageSlug: page.slug },
            })
          },
          starred,
        })
      } else if (starred.entityType === 'cycle') {
        const cycle = cycleMap.get(starred.entityId)
        if (!cycle) continue
        const parentProject = projectMap.get(cycle.projectId)
        if (!parentProject) continue
        results.push({
          id: starred.id,
          entityType: 'cycle',
          entityId: cycle.id,
          name: cycle.name,
          icon: <RefreshCw className="size-3.5 text-muted-foreground shrink-0" />,
          onClick: () => navigate({
            to: '/$workspaceSlug/projects/$projectKey/cycles/$cycleId',
            params: { workspaceSlug, projectKey: parentProject.projectKey.toLowerCase(), cycleId: cycle.id },
          }),
          starred,
        })
      }
    }

    return results
  }, [starredItems, projects, tasks, pages, cycles, workspaceSlug, navigate, t])

  if (isLoading || resolved.length === 0) return null

  return (
    <section aria-label={t('sidebar.favorites')} className="py-1.5">
      <SectionHeader label={t('sidebar.favorites')} />
      <ul className="flex flex-col gap-px px-2">
        {resolved.map((item) => (
          <li key={item.id}>
            <div className={rowClass(false, 'cursor-pointer')} onClick={item.onClick}>
              {item.icon}
              <button type="button" className="min-w-0 flex-1 truncate text-left" onClick={(e) => { e.stopPropagation(); item.onClick() }}>
                {item.name}
              </button>
              <button
                type="button"
                aria-label={t('sidebar.removeFavoriteAria')}
                className="flex size-5 shrink-0 items-center justify-center rounded opacity-0 transition-opacity hover:bg-sidebar-accent group-hover/row:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
                onClick={(e) => {
                  e.stopPropagation()
                  void toggleStar(item.entityType, item.entityId, item.starred)
                }}
              >
                <Star className="size-3.5 fill-yellow-400 text-yellow-400" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ─── Main sidebar ─────────────────────────────────────────────────────────────

export function ProjectSidebar({ workspaceId, workspaceSlug, routeSpaceId, onDrillIn }: ProjectSidebarProps) {
  const { t } = useTranslation('projects')
  const { t: tCommon } = useTranslation('common')
  const { state } = useSidebar()
  const [isAddSpaceOpen, setIsAddSpaceOpen] = useState(false)

  const { projects: rootProjects, isLoading: projectsLoading } = useRootProjects()
  const { createProject } = useProjectMutations()
  // Private per-user Space ordering. Empty for anyone who has never reordered,
  // which keeps the A–Z default below.
  const { spaceOrders } = useSpaceOrders()

  const navigate = useNavigate()
  const spaceFormSchema = useMemo(() => buildSpaceFormSchema(t), [t])
  const form = useForm<SpaceFormData>({
    resolver: zodResolver(spaceFormSchema),
    defaultValues: {
      name: '',
      icon: null,
      iconType: null,
      iconColor: null,
      projectName: '',
      projectKey: '',
      projectIcon: null,
      projectIconType: null,
      projectIconColor: null,
    },
  })

  // Auto-suggest the project key from the project name until the user edits
  // it manually. Once they touch the key field, leave it alone.
  const projectKeyDirtyRef = useRef(false)
  const projectName = form.watch('projectName')
  useEffect(() => {
    if (projectKeyDirtyRef.current) return
    const derived = projectName.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3)
    form.setValue('projectKey', derived, { shouldValidate: false })
  }, [projectName, form])

  const spaceRanks = useMemo(
    () =>
      spaceOrders
        .filter((order) => !order.isDeleted)
        .map((order) => ({ projectId: order.projectId, sortOrder: order.sortOrder })),
    [spaceOrders],
  )

  const scopedSpaces = useMemo<Array<ProjectDocument>>(() => {
    const visible = (rootProjects ?? []).filter((p) => {
      if (p.isDeleted) return false
      if (workspaceId && p.envId !== workspaceId) return false
      return true
    })
    // No rank rows → A–Z, the behaviour Spaces had before private ordering.
    // Ranked Spaces lead; new or restored Spaces trail, A–Z among themselves.
    return sortSpacesByPrivateOrder(visible, spaceRanks)
  }, [rootProjects, workspaceId, spaceRanks])

  const {
    handleReorder: handleSpaceReorder,
    draggingId: draggingSpaceId,
    setDraggingId: setDraggingSpaceId,
    dropIndicator: spaceDropIndicator,
    setDropIndicator: setSpaceDropIndicator,
  } = useSpaceReorder(scopedSpaces, spaceOrders)

  const handleCreateSpace = async (data: SpaceFormData) => {
    if (!workspaceSlug || !workspaceId) { toast.error(t('sidebar.toast.missingWorkspace')); return }

    const spaceId = crypto.randomUUID()
    const spaceKey = deriveUniqueSpaceKey(data.name, scopedSpaces)

    try {
      await createProject({
        id: spaceId,
        projectKey: spaceKey,
        name: data.name,
        icon: data.icon,
        iconType: data.iconType,
        iconColor: data.iconColor,
      })
    } catch (error) {
      const fallback = t('sidebar.toast.spaceCreateFailed')
      toast.error(error instanceof Error ? error.message || fallback : fallback)
      return
    }

    // Space succeeded — surface project failures inline so the user can fix
    // the project key without re-typing the space details.
    try {
      await createProject({
        id: crypto.randomUUID(),
        projectKey: data.projectKey,
        name: data.projectName,
        icon: data.projectIcon,
        iconType: data.projectIconType,
        iconColor: data.projectIconColor,
        parentProjectId: spaceId,
      })
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : t('sidebar.toast.projectCreateFailed')
      form.setError('projectKey', { type: 'server', message })
      toast.success(t('sidebar.toast.spaceCreated'))
      return
    }

    form.reset()
    projectKeyDirtyRef.current = false
    setIsAddSpaceOpen(false)
    toast.success(t('sidebar.toast.spaceCreated'))
    navigate({
      to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
      params: {
        workspaceSlug,
        spaceKey: spaceKey.toLowerCase(),
        projectKey: data.projectKey.toLowerCase(),
      },
    })
  }

  return (
    <Dialog
      open={isAddSpaceOpen}
      onOpenChange={(open) => {
        setIsAddSpaceOpen(open)
        if (!open) {
          form.reset()
          projectKeyDirtyRef.current = false
        }
      }}
    >
      {workspaceSlug && state !== 'collapsed' && (
        <FavoritesSection workspaceSlug={workspaceSlug} />
      )}

      {/* Pinned views (design 20b) sit between Favorites and Spaces (21 C). */}
      {workspaceSlug && state !== 'collapsed' && <PinnedViewsSection workspaceSlug={workspaceSlug} />}

      {workspaceSlug && state !== 'collapsed' && (
        <SpacesList
          spaces={scopedSpaces}
          isLoading={projectsLoading}
          routeSpaceId={routeSpaceId}
          onDrillIn={onDrillIn}
          onAddSpace={() => setIsAddSpaceOpen(true)}
          orderedIds={scopedSpaces.map((space) => space.id)}
          draggingId={draggingSpaceId}
          onDragStart={setDraggingSpaceId}
          onDragEnd={() => setDraggingSpaceId(null)}
          onReorder={handleSpaceReorder}
          dropIndicator={spaceDropIndicator}
          onDropIndicatorChange={setSpaceDropIndicator}
        />
      )}
      {workspaceSlug && state === 'collapsed' && (
        <SpacesRail spaces={scopedSpaces} workspaceSlug={workspaceSlug} routeSpaceId={routeSpaceId} />
      )}

      {/* Add Space Dialog */}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('sidebar.addSpace.title')}</DialogTitle>
          <DialogDescription>{t('sidebar.addSpace.description')}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-5" onSubmit={form.handleSubmit(handleCreateSpace)}>
          <div className="grid gap-2">
            <Label htmlFor="space-name">{t('sidebar.addSpace.nameLabel')}</Label>
            <div className="relative">
              <UnifiedIconPicker
                iconType={form.watch('iconType')}
                icon={form.watch('icon')}
                iconColor={form.watch('iconColor')}
                side="right"
                sideOffset={12}
                onValueChange={(value) => {
                  form.setValue('icon', value?.icon ?? null)
                  form.setValue('iconType', value?.iconType ?? null)
                  form.setValue('iconColor', value?.iconColor ?? null)
                }}
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute left-1 top-1/2 -translate-y-1/2 h-7 w-7 hover:bg-muted"
                  aria-label={t('sidebar.addSpace.iconAria')}
                  disabled={form.formState.isSubmitting}
                >
                  {form.watch('icon') ? (
                    <ProjectIcon iconType={form.watch('iconType')} icon={form.watch('icon')} iconColor={form.watch('iconColor')} size={16} />
                  ) : (
                    <Folder className="size-4 text-muted-foreground" />
                  )}
                </Button>
              </UnifiedIconPicker>
              <Input
                id="space-name"
                {...form.register('name')}
                placeholder={t('sidebar.addSpace.namePlaceholder')}
                autoFocus
                disabled={form.formState.isSubmitting}
                className="pl-10"
              />
            </div>
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="grid gap-4 rounded-lg border bg-muted/30 p-4">
            <div className="grid gap-2">
              <div className="grid gap-0.5">
                <Label htmlFor="space-project-name">{t('sidebar.addSpace.projectNameLabel')}</Label>
                <p className="text-xs text-muted-foreground">{t('sidebar.addSpace.projectNameSubtitle')}</p>
              </div>
              <div className="relative">
                <UnifiedIconPicker
                  iconType={form.watch('projectIconType')}
                  icon={form.watch('projectIcon')}
                  iconColor={form.watch('projectIconColor')}
                  side="right"
                  sideOffset={12}
                  onValueChange={(value) => {
                    form.setValue('projectIcon', value?.icon ?? null)
                    form.setValue('projectIconType', value?.iconType ?? null)
                    form.setValue('projectIconColor', value?.iconColor ?? null)
                  }}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute left-1 top-1/2 -translate-y-1/2 h-7 w-7 hover:bg-muted"
                    aria-label={t('sidebar.addSpace.projectIconAria')}
                    disabled={form.formState.isSubmitting}
                  >
                    {form.watch('projectIcon') ? (
                      <ProjectIcon iconType={form.watch('projectIconType')} icon={form.watch('projectIcon')} iconColor={form.watch('projectIconColor')} size={16} />
                    ) : (
                      <Target className="size-4 text-muted-foreground" />
                    )}
                  </Button>
                </UnifiedIconPicker>
                <Input
                  id="space-project-name"
                  {...form.register('projectName')}
                  placeholder={t('sidebar.addSpace.projectNamePlaceholder')}
                  disabled={form.formState.isSubmitting}
                  className="pl-10 bg-background"
                />
              </div>
              {form.formState.errors.projectName && (
                <p className="text-xs text-destructive">{form.formState.errors.projectName.message}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="space-project-key">{t('sidebar.addSpace.projectKeyLabel')}</Label>
              <Input
                id="space-project-key"
                {...form.register('projectKey')}
                onChange={(e) => {
                  projectKeyDirtyRef.current = true
                  form.setValue('projectKey', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))
                }}
                placeholder={t('sidebar.addSpace.projectKeyPlaceholder')}
                maxLength={10}
                disabled={form.formState.isSubmitting}
                className="w-1/2 font-mono bg-background"
              />
              <p className="text-xs text-muted-foreground">{t('sidebar.addSpace.projectKeyHint')}</p>
              {form.formState.errors.projectKey && (
                <p className="text-xs text-destructive">{form.formState.errors.projectKey.message}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={form.formState.isSubmitting}>{tCommon('actions.cancel')}</Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? t('sidebar.addSpace.submitting') : t('sidebar.addSpace.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}